import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { isFamylocateCohort } from 'helpers'

import {
  sendUserConfigAction,
  setCookieConsentAction,
} from 'root-redux/actions/user'
import { selectCurrentVariantCohortToUse } from 'root-redux/selects/common'

import { eventLogger } from 'services/eventLogger.service'

import { CookiePolicyLink } from 'components/CookiePolicyLink'
import { SvgImage } from 'components/SvgImage'

import famylocateLogo from 'assets/images/famylocate-header-logo-v2.png'
import logoImg from 'assets/images/logo.png'
import crossIcon from 'assets/images/sprite/cross-2.svg'

import { CookieConsentOption } from 'root-constants/common'

import { StyledCookieConsent as S } from './CookieConsent.styles'
import { ALL_COOKIE_CONSENT_TYPES, Source } from './constants'

export const CookieConsent = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const cohort = useSelector(selectCurrentVariantCohortToUse)

  const [isCookieConsentVisible, setIsCookieConsentVisible] = useState(true)
  const [areSettingsOn, setAreSettingsOn] = useState(false)
  const [cookieConsentTypes, setCookieConsentTypes] = useState<string[]>([])

  const isFamyLocate = isFamylocateCohort(cohort)

  useEffect(() => {
    isCookieConsentVisible && eventLogger.logCookiesConsentShown()
  }, [isCookieConsentVisible])

  const toggleSettingsVisibility = () => {
    areSettingsOn
      ? eventLogger.logCookiesConsentSettingsScreenClose()
      : eventLogger.logCookiesConsentSettingsTap()
    setAreSettingsOn((prev) => !prev)
  }

  const handleSubmit = (userAnswer: string[]) => {
    setIsCookieConsentVisible(false)
    dispatch(setCookieConsentAction(userAnswer))
    dispatch(
      sendUserConfigAction({
        cookie_consent: userAnswer,
      }),
    )
  }

  const handleChange = ({ target: { value } }) => {
    if (cookieConsentTypes.includes(value)) {
      eventLogger.logCookiesConsentToggleDisable(value)
      setCookieConsentTypes((prev) => prev.filter((item) => item !== value))
      return
    }

    eventLogger.logCookiesConsentToggleEnable(value)
    setCookieConsentTypes((prev) => [...prev, value])
  }

  if (!isCookieConsentVisible) {
    return null
  }

  return (
    <S.Wrapper fullHeight={areSettingsOn} hasUnfullWidth={areSettingsOn}>
      {areSettingsOn && (
        <S.Header>
          <img
            height="28"
            src={isFamyLocate ? famylocateLogo : logoImg}
            alt="logo"
          />
          <S.CloseButton onClick={toggleSettingsVisibility}>
            <SvgImage svg={crossIcon} width={24} />
          </S.CloseButton>
        </S.Header>
      )}
      {areSettingsOn && (
        <>
          <S.Container>
            <S.Title marginBottom={12}>
              {t('cookieConsent.privacyCenterTitle')}
            </S.Title>
            <S.Description
              hasUnfullWidth={areSettingsOn}
              isFamyLocateCohort={isFamyLocate}
            >
              {t('cookieConsent.privacyCenterDescription')}
            </S.Description>
            <S.PrimaryButton
              hasUnfullWidth={areSettingsOn}
              onClick={() => {
                eventLogger.logCookiesConsentAcceptAllTap(Source.SETTINGS)
                handleSubmit(ALL_COOKIE_CONSENT_TYPES)
              }}
            >
              {t('cookieConsent.allowAll')}
            </S.PrimaryButton>
            <S.SettingsTitle>
              {t('cookieConsent.consentManagement')}
            </S.SettingsTitle>
            <S.Settings>
              <S.SettingItem>
                <S.Setting
                  onClick={(event) => {
                    if (event.currentTarget.hasAttribute('open')) return

                    eventLogger.logCookiesConsentExpandOptionTap(
                      CookieConsentOption.ADVERTISING_AND_TARGETING,
                    )
                  }}
                >
                  <S.SettingName>
                    <span>{t('cookieConsent.advertisingCookies')}</span>
                  </S.SettingName>
                  <S.SettingDetails>
                    {t('cookieConsent.advertisingCookiesDetails')}
                  </S.SettingDetails>
                </S.Setting>
                <S.Switch
                  value={CookieConsentOption.ADVERTISING_AND_TARGETING}
                  onChange={handleChange}
                  isChecked={cookieConsentTypes.includes(
                    CookieConsentOption.ADVERTISING_AND_TARGETING,
                  )}
                />
              </S.SettingItem>
              <S.SettingItem>
                <S.Setting
                  onClick={(event) => {
                    if (event.currentTarget.hasAttribute('open')) return

                    eventLogger.logCookiesConsentExpandOptionTap(
                      CookieConsentOption.FUNCTIONAL_COOKIES,
                    )
                  }}
                >
                  <S.SettingName>
                    <span>{t('cookieConsent.functionalCookies')}</span>
                  </S.SettingName>
                  <S.SettingDetails>
                    {t('cookieConsent.functionalCookiesDetails')}
                  </S.SettingDetails>
                </S.Setting>
                <S.Switch
                  value={CookieConsentOption.FUNCTIONAL_COOKIES}
                  onChange={handleChange}
                  isChecked={cookieConsentTypes.includes(
                    CookieConsentOption.FUNCTIONAL_COOKIES,
                  )}
                />
              </S.SettingItem>
              <S.SettingItem>
                <S.Setting
                  onClick={(event) => {
                    if (event.currentTarget.hasAttribute('open')) return

                    eventLogger.logCookiesConsentExpandOptionTap(
                      CookieConsentOption.ANALYTICS_AND_PERFORMANCE,
                    )
                  }}
                >
                  <S.SettingName>
                    <span>{t('cookieConsent.analyticalCookies')}</span>
                  </S.SettingName>
                  <S.SettingDetails>
                    {t('cookieConsent.analyticalCookiesDetails')}
                  </S.SettingDetails>
                </S.Setting>
                <S.Switch
                  value={CookieConsentOption.ANALYTICS_AND_PERFORMANCE}
                  onChange={handleChange}
                  isChecked={cookieConsentTypes.includes(
                    CookieConsentOption.ANALYTICS_AND_PERFORMANCE,
                  )}
                />
              </S.SettingItem>
              <S.SettingItem>
                <S.Setting>
                  <S.SettingName>
                    <span>{t('cookieConsent.necessaryCookies')}</span>
                    <S.ActiveLabel>
                      {t('cookieConsent.activeLabel')}
                    </S.ActiveLabel>
                  </S.SettingName>
                  <S.SettingDetails>
                    {t('cookieConsent.necessaryCookiesDetails')}
                  </S.SettingDetails>
                </S.Setting>
              </S.SettingItem>
            </S.Settings>
          </S.Container>
          <S.Divider />
        </>
      )}

      <S.SettingsOffContainer>
        {!areSettingsOn && (
          <>
            <S.DescriptionBlock>
              <S.Title>{t('cookieConsent.title')}</S.Title>
              <S.Description
                hasUnfullWidth={areSettingsOn}
                isFamyLocateCohort={isFamyLocate}
              >
                {t('cookieConsent.description')}{' '}
                <CookiePolicyLink title="cookieConsent.cookieNotice" />
              </S.Description>
            </S.DescriptionBlock>
            <S.PrimaryButton
              hasUnfullWidth={areSettingsOn}
              onClick={() => {
                eventLogger.logCookiesConsentAcceptAllTap(Source.BANNER)
                handleSubmit(ALL_COOKIE_CONSENT_TYPES)
              }}
            >
              {t('cookieConsent.acceptAll')}
            </S.PrimaryButton>
            <S.SecondaryButton
              hasUnfullWidth={areSettingsOn}
              onClick={toggleSettingsVisibility}
            >
              {t('cookieConsent.settings')}
            </S.SecondaryButton>
          </>
        )}

        {areSettingsOn && (
          <>
            <S.PrimaryButton
              hasUnfullWidth={areSettingsOn}
              onClick={() => {
                eventLogger.logCookiesConsentAcceptAllTap(Source.SETTINGS)
                handleSubmit(ALL_COOKIE_CONSENT_TYPES)
              }}
            >
              {t('cookieConsent.allowAll')}
            </S.PrimaryButton>

            <S.SecondaryButton
              hasUnfullWidth={areSettingsOn}
              onClick={() => {
                eventLogger.logCookiesConsentSettingsConfirmChoice(
                  `${
                    cookieConsentTypes.toString() ||
                    CookieConsentOption.REJECT_ALL
                  }`,
                )
                handleSubmit(cookieConsentTypes)
              }}
            >
              {t('cookieConsent.confirmChoice')}
            </S.SecondaryButton>
          </>
        )}
        <S.SecondaryButton
          hasUnfullWidth={areSettingsOn}
          onClick={() => {
            handleSubmit([])
            eventLogger.logCookiesConsentRejectAllTap(
              areSettingsOn ? Source.SETTINGS : Source.BANNER,
            )
          }}
        >
          {t('cookieConsent.rejectAll')}
        </S.SecondaryButton>
      </S.SettingsOffContainer>
    </S.Wrapper>
  )
}
